// =====================================================================================
// INVRT Radar — web console — account — Prices tab
//
// The organisation's own rate card and the surfaces that are always free. Moved verbatim
// out of AccountPage.tsx in the C1 split; the page shell registers it under the tab id
// "pricing".
// =====================================================================================

import { usePricing } from "@/hooks/useApi";
import { ErrorState, LoadingState } from "@/components/States";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { formatCredits, formatDateTime, humaniseAction } from "@/lib/format";

export function PricingPanel() {
  const pricing = usePricing();

  if (pricing.isLoading) return <LoadingState label="Loading your price list…" />;
  if (pricing.isError) {
    return <ErrorState error={pricing.error} onRetry={() => void pricing.refetch()} />;
  }
  if (!pricing.data) return null;

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle>What things cost</CardTitle>
          <p className="mt-1 text-sm text-ink-muted">{pricing.data.note}</p>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Action</TableHeaderCell>
                <TableHeaderCell className="text-right">Credits each</TableHeaderCell>
                <TableHeaderCell>In force since</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pricing.data.actions.map((entry) => (
                <TableRow key={entry.action}>
                  <TableCell className="font-medium">{humaniseAction(entry.action)}</TableCell>
                  <TableCell className="text-right font-mono">
                    {formatCredits(entry.credits_per_unit)}
                  </TableCell>
                  <TableCell className="text-ink-muted">
                    {formatDateTime(entry.effective_from)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Free, always</CardTitle>
          <p className="mt-1 text-sm text-ink-muted">
            These never touch your balance. Search as much as you like.
          </p>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-wrap gap-2">
            {pricing.data.free_surfaces.map((surface) => (
              <li key={surface}>
                <Badge tone="success">{humaniseAction(surface)}</Badge>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
